// OWNER: CONTENT agent. Checks a campaign template or the curriculum against the house rules (SPEC §14,
// playbook-training.md §3-§4, compliance-verification.md). Returns plain-English problems; an empty list means OK.
// Used by tests/content-templates.test.ts (and the content-price-policy, content-review-scripts and content-leadgen
// tests), by CORE's draft-to-live check (src/worker/routes/campaigns.ts goLiveProblems) on an edited campaign script, and
// by the admin page's copy of that check (web/src/admin/validate.ts houseRuleProblems). It reads script text only; a
// setter's live speech is graded by the practice scorer (src/worker/ai/scorecard.ts) and the nightly call review.
import type { CampaignTemplate, PersonaKind, TrainingItem } from '../src/shared/api';
import { OBJECTIONS } from '../src/shared/api';
import { PLACEHOLDERS, placeholdersIn } from './placeholders';
import { OPENER_NEVER } from '../src/shared/opener-lint';
import { ADS_RE, BANNED_PHRASES, PRICE_ON_SITE_RE, SETUP_WAIVER_RE, adsMention, isCannabisNiche } from '../src/shared/offer-guards';
import { APPROVED_PRICE_LINE } from './campaigns/shared';

/** Spoken pace used for every length rule (playbook-training.md §4.3: about 140 words a minute). */
export const WORDS_PER_MINUTE = 140;
/** Seconds to read a phone number aloud, slowly. */
export const SECONDS_PER_NUMBER = 4;

/** Words a setter actually says: [setter cues] removed, each {placeholder} counted as one word. */
export function spokenWords(text: string): number {
  const stripped = text.replace(/\[[^\]]*\]/g, ' ');
  return stripped.split(/\s+/).filter((w) => /[A-Za-z0-9{]/.test(w)).length;
}

/** Estimated seconds to say a recorded message: words at 140 a minute, plus 4 s for each number read out. */
export function spokenSeconds(text: string): number {
  const numbers = (text.match(/\{callback\}/g) ?? []).length;
  const words = spokenWords(text) - numbers;
  return Math.round(((words * 60) / WORDS_PER_MINUTE + numbers * SECONDS_PER_NUMBER) * 10) / 10;
}

// Things a script must never say. Checked against every spoken or emailed line of a template or campaign script, not
// against complianceNotes (which quote some of them as "never say" examples) or persona briefs (the prospect may say
// anything), and never against a setter's live speech (a setter may quote a US price on the spot, PJ 2026-10-07).
// `said: true` rules look only at what is said: [setter cues] are removed first, so a cue may still name a competitor's
// price ("[They want a $99 tool they set up themselves: ...]", ca-auto-repair-front-desk's AutoLeap card). `cues: true`
// rules also read each [cue] that has no negation word (CUE_NEGATION): "[Never say free.]" quotes the rule, "[Tell them
// the setup is free.]" is a line to say. `repeat: true` rules (BANNED_PHRASES) are skipped where a rule above already
// reported the same words.
const NEVER_SAY: Array<{ re: RegExp; why: string; said?: true; cues?: true; repeat?: true }> = [
  { re: /bad time/i, why: '"bad time?" opener is banned' },
  { re: /\b62\s?%|\b85\s?%|\b80\s?%|126\s?k|\$\s?126|\$1,200 per/i, why: 'refuted statistic' },
  { re: /\d+(\.\d+)?\s?%/, why: 'percent figure (no stats in scripts)' },
  { re: /never miss/i, why: '"never miss" promise' },
  { re: /\bguarantee/i, why: 'guarantee language' },
  { re: /number one|#1\b|rank (you )?first|top of google/i, why: 'ranking promise' },
  { re: /\bour clients\b|\bclients like\b|\bwe work with\b|\bwe('ve| have) helped\b|\bcase stud/i, why: 'implies clients or proof Valistix does not have' },
  { re: /\bwe (rang|called|tested|phoned)\b|test[- ]call/i, why: 'claims or offers a covert test call' },
  { re: /\b(heal|cure|relief|anxiety|insomnia|therapeutic|medicinal|wellness benefits?)\b/i, why: 'health or cannabis-effect claim' },
  { re: /\b(double|triple|more) (your )?(sales|revenue|orders|customers|jobs)\b/i, why: 'results promise' },
  // The offer PJ settled on 2026-10-07 (ai-agency/web/DECISIONS-2026-10-07-stages.md; INTERNAL
  // ai-agency/sales/offer-2026-10-07/PRICE-BOOK.md "Who may quote"): tiers and prices are shown only in the meeting, by the
  // specialist, and the scripts say no number (a setter may still quote a US price live, at or above the floors; the
  // scorer and the nightly review handle that, not this check). "How much?" gets PRICE_ANSWER, or APPROVED_PRICE_LINE once
  // PJ approves one (that exact line is the one figure allowed: checkNeverSay takes it out before these rules run).
  // Founding terms are off.
  // A bare figure counts too: "297 a month", "497 setup", "It's 297 to start", "It was 497", "two ninety-seven". A
  // three-digit number that is a count, a time or a phone number passes ("about 300 calls a week", "7:30", "1,000
  // minutes", "(866) 413-5421", "24/7").
  { re: /\$\s?\d+(,\d{3})*(\.\d+)?|\b\d[\d,.]*\s?(dollars?|bucks)\b|\b(hundred|thousand) (dollars|bucks)\b|\b\d[\d,]*(?:\.\d+)?\s?(?:\/\s?mo(?:nth)?\b|(?:a|per|an|each|every)[\s-]?mo(?:nth)?\b|monthly\b|per[\s-]month\b|(?:setup|set-up|up front|upfront)\b)|(?<![\d(\-.:\/])\b\d{3}(?:,\d{3})*\b(?![\d)\-:\/]|\.\d|\s*(?:business days?|days?|weeks?|months?|years?|minutes?|hours?|seconds?|rings?|calls?|trucks?|bays?|techs?|cars?|jobs?|reviews?|customers?|people|times|words?|characters?)\b)|\b(?:two|four|nine)[- ]ninety[- ]seven\b/i, why: 'price figure (the scripts say no number; "How much?" gets the specialist line, 2026-10-07)', said: true },
  // Case-sensitive: the plan and tier names, old and new; "your front desk" (the counter) is fine.
  { re: /\b(Front Desk|Growth Desk|Full Stack|Personal Assistant|Command Center|Storefront|Spotlight|Marquee|Ignite|Accelerate|Full Throttle)\b/, why: 'names a plan or tier (tiers are for the specialist in the meeting, 2026-10-07)', said: true },
  { re: /\bfounding\b/i, why: 'founding terms (off since 2026-10-07)' },
  // What the nightly call review grades as offer violations (ai-agency/sales/call-review/REVIEW.md offer_violations; PJ,
  // 2026-10-07): nothing is "free" on a call, in any form (the meeting, the check or anything else; "toll-free" describes a
  // phone number and passes), nothing "costs you nothing", we never say we already built them an agent, and no founding-
  // client lines. Said parts, and [cues] with no negation word, so a cue may still quote them as "never" examples.
  { re: /(?<!toll[- ])\bfree\b/i, why: '"free" (never said on a call, in any form, 2026-10-07)', said: true, cues: true },
  { re: /\bcosts? (?:you )?nothing\b/i, why: '"costs you nothing" (a "free" claim, 2026-10-07)', said: true, cues: true },
  { re: /\bbuilt and ready\b/i, why: '"built and ready" (never say we already built them an agent, 2026-10-07)', said: true, cues: true },
  { re: /\bfirst (?:five|few)\b|\b(?:one of|among) our (?:very )?first\b/i, why: 'founding-client line ("first five", "first few", "one of our first"; founding terms are off, 2026-10-07)', said: true, cues: true },
  // PJ, 2026-10-07: no unapproved price line ("depends on your calls", sales/offer-2026-10-07/PRICE-BOOK.md, not approved),
  // no "it's on our website" (valistix.com shows no prices), no former price ("normally $X"), and not the old transfer
  // wording: every Get Answered tier puts urgent calls through (web/DECISIONS-2026-10-07-stages.md).
  { re: /\bdepends on your calls\b/i, why: '"depends on your calls" (not an approved price line; "How much?" gets the specialist line, 2026-10-07)', said: true },
  { re: /\bon our website\b/i, why: '"on our website" (valistix.com shows no prices, 2026-10-07)', said: true, cues: true },
  // ...and the website as a price source in any other words ("see pricing at valistix.com", "check valistix.com for
  // pricing"), the drafters' rule (shared/offer-guards.ts PRICE_ON_SITE_RE); not reported twice with "on our website".
  { re: PRICE_ON_SITE_RE, why: 'points to the website for a price (valistix.com shows no prices, 2026-10-07)', said: true, cues: true, repeat: true },
  { re: /\bnormally\b/i, why: '"normally" (never a former price, "normally $X", 2026-10-07)', said: true, cues: true },
  { re: /(?:puts|passes) (?:people|callers) (?:through )?to [^.]{0,30} when they ask/i, why: 'old transfer wording (it puts urgent calls through, 2026-10-07)', said: true },
  // The setup is never "waived" (or "free", "on the house", "complimentary"; a customer's "liability waiver" passes:
  // shared/offer-guards.ts SETUP_WAIVER_RE): a no-setup start at the list monthly, first month final, is said with its own
  // terms beside the setup start (PRICE-BOOK.md line 26 and "Floors"). No results or lead numbers ("more calls", "20 new
  // leads"). And the offer lines nobody may say, in an email, a text or a script (shared/offer-guards.ts BANNED_PHRASES).
  { re: SETUP_WAIVER_RE, why: 'setup waiver (never call the setup "waived" or "free"; a no-setup start is said with its own terms, 2026-10-07)', said: true, cues: true },
  // A promise, not a question: "You'll get more calls" fails; "Do you get more calls than you can pick up on a Saturday?"
  // passes.
  { re: /\b(?:get|gets|getting|bring|brings|win|wins|see|you['’]ll (?:get|see|book))\s+(?:you\s+)?more (?:calls|leads|bookings|booked jobs)\b(?![^.?]*\?)|\b\d+\s+(?:new |more )?leads\b|\bmore (?:leads|bookings|booked jobs)\b(?![^.?]*\?)/i, why: 'results / lead-number promise', said: true },
  ...BANNED_PHRASES.map((b) => ({ re: b.re, why: b.problem, said: true as const, repeat: true as const })),
];

/** A [cue] with one of these words quotes a rule ("[Never say free.]"); one without is read like a line to say. */
const CUE_NEGATION = /\bnever\b|\bno\b|\bnot\b|\bdon['’]?t\b/i;

const FIRST_MINUTE_AI = /\bAI\b|artificial intelligence|robot/i;

/**
 * LEADGEN-SPEC §7.4 and §13 CONTENT: OPENER_NEVER (src/shared/opener-lint.ts, the same rule the hook engine and the brief
 * use) beside NEVER_SAY. Owners hang up on the Google-listing scam script ("verify your Google listing", "your profile
 * will be suspended"), so none of its words may be in what a setter says in the first seconds: every opener and every
 * gatekeeper line (the opener when someone else answers). Setter cues in [brackets] aren't said, so they aren't checked;
 * quoted lines after a cue are. Later parts of the call (reason, questions, pitch, ask) may name Google.
 */
export function openerNeverWord(text: string): string | null {
  const said = text.replace(/\[[^\]]*\]/g, ' ');
  return said.match(OPENER_NEVER)?.[0] ?? null;
}

/**
 * The sentences a setter says before the first [cue] or blank line. A sentence ends at . ? or ! followed by a space and
 * a capital (or a placeholder), so "Valistix Inc., and ..." stays one sentence and "Valistix Inc. I'm ..." is two.
 */
export function spokenSentences(text: string): string[] {
  const said = text.split(/\n\s*\n|\[/)[0] ?? '';
  return said.split(/(?<=[.?!])\s+(?=["“'(]?[A-Z{])/).map((x) => x.trim()).filter(Boolean);
}

// US openers (PJ, 2026-10-03): the state notes (StateRule.setterNote) are tips on the lead card, not lines a setter must
// say. No opener has to say "sales call", ask permission, carry a "[If no:] ... End the call." cue or run an owner check
// (each is still fine to have; "Is it OK...?" sells). What stays: the setter's name and "Valistix" in the first sentence,
// the Default filling on every lead, and a short first sentence. The hard rules (hours, closed and hand-pick states,
// do-not-call, attempt caps) are enforced by the dialer, not by the script.
/** Name and reason inside about 15 seconds (140 words a minute): a long first sentence loses the owner. */
const FIRST_SENTENCE_MAX_WORDS = 35;

const ALLOWED: Record<string, readonly string[]> = {
  openers: ['setter', 'business', 'dm_name', 'observation'],
  spoken: ['setter', 'business', 'dm_name'],
  objections: ['setter', 'business'],
  voicemailsLive: ['setter', 'business', 'callback'],
  voicemailsDrop: ['setter', 'callback'],
  emails: ['first_name', 'business', 'setter', 'observation', 'calendly_link', 'mailing_address'],
};

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const PERSONA_KINDS: readonly PersonaKind[] = ['gatekeeper', 'lukewarm', 'hostile'];

/** An opener that fills on every lead (only {setter} and {business}), so the dialer may show or rotate it on any lead. */
export function isEveryLeadOpener(text: string): boolean {
  return placeholdersIn(text).every((n) => n === 'setter' || n === 'business');
}

/**
 * US openers. Every opener says the setter's name ({setter}) and "Valistix" in its first sentence. Opener 1 is the
 * default shown on every lead, so it uses only {setter}/{business}. Since 2026-10-01 the dialer rotates every opener
 * that fills on every lead (opener A/B), so opener 1 and each of those keep the first sentence short (about 15 s).
 * Nothing here asks for "sales call", a permission question, a "no ends the call" cue or an owner check (PJ, 2026-10-03).
 */
export function usOpenerProblems(openers: ReadonlyArray<{ label: string; text: string }>): string[] {
  const p: string[] = [];
  openers.forEach((o, i) => {
    const first = spokenSentences(o.text)[0] ?? '';
    const missing: string[] = [];
    if (!first.includes('{setter}')) missing.push('your name ({setter})');
    if (!first.includes('Valistix')) missing.push('"Valistix"');
    if (missing.length) p.push(`opener ${i + 1} needs ${missing.join(' and ')} in the first sentence`);
  });
  const d = openers[0];
  if (!d) return p;
  const extra = placeholdersIn(d.text).filter((n) => n !== 'setter' && n !== 'business');
  if (extra.length) p.push(`opener 1 is the default for every lead, so it can use only {setter} and {business}, not {${extra.join('}, {')}}`);
  openers.forEach((o, i) => {
    if (i > 0 && !isEveryLeadOpener(o.text)) return; // shown only when its extra facts are known; never rotated in
    const words = spokenWords(spokenSentences(o.text)[0] ?? '');
    if (words > FIRST_SENTENCE_MAX_WORDS)
      p.push(`opener ${i + 1}'s first sentence is ${words} words; keep your name, Valistix and the reason inside about 15 seconds (${FIRST_SENTENCE_MAX_WORDS} words)`);
  });
  return p;
}

export function validateTemplate(t: CampaignTemplate): string[] {
  const p: string[] = [];
  const s = t.script;
  const where = (part: string) => `${t.key} ${part}`;

  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(t.key)) p.push(`${t.key}: key must be lowercase-hyphenated`);
  for (const f of ['name', 'niche', 'offer'] as const) if (!t[f]?.trim()) p.push(where(`${f} is empty`));
  if (!s.summary.trim()) p.push(where('summary is empty'));

  // placeholders: known names only, and only where the app can fill them
  const checkPlaceholders = (part: string, text: string, allowed: readonly string[]) => {
    for (const name of placeholdersIn(text)) {
      if (!(name in PLACEHOLDERS)) p.push(where(`${part} uses unknown placeholder {${name}}`));
      else if (!allowed.includes(name)) p.push(where(`${part} uses {${name}}, which is not filled there`));
    }
  };
  const cannabis = isCannabisNiche(t.niche);
  const checkNeverSay = (part: string, text: string) => {
    // what is said: no [cues], and not the approved price line (null until PJ approves one, 2026-10-07), which only a US
    // Get Answered (front_desk) template may say (the drafters' rule too: salesFlow.ts, smsDraft.ts priceLine)
    const cuesOut = text.replace(/\[[^\]]*\]/g, ' ');
    const said = APPROVED_PRICE_LINE && t.country === 'US' && t.offer === 'front_desk' ? cuesOut.split(APPROVED_PRICE_LINE).join(' ') : cuesOut;
    const cues = [...text.matchAll(/\[([^\]]*)\]/g)].map((m) => m[1] ?? '').filter((c) => !CUE_NEGATION.test(c)).join('\n');
    const reported: string[] = [];
    for (const rule of NEVER_SAY) {
      const inSaid = (rule.said ? said : text).match(rule.re);
      const m = inSaid ?? (rule.cues ? cues.match(rule.re) : null);
      if (!m) continue;
      const words = m[0].toLowerCase();
      if (rule.repeat && reported.some((r) => r.includes(words) || words.includes(r))) continue;
      reported.push(words);
      p.push(where(`${part}: ${rule.why} ("${m[0]}"${inSaid ? '' : ', in a [cue]'})`));
    }
    // Ads are never offered to a cannabis business, and since PJ 2026-10-19 a cannabis script names no ads at all, not
    // even to say they're banned ("Google won't run ads for cannabis" fails too). Since PJ 2026-10-20 a store's Get
    // Booked is email and text campaigns to its own customers who signed up, so the name passes; every ads word still
    // fails. An offer is reported by the word shared/offer-guards.ts adsMention finds; a "won't run ads" line by its first
    // ADS_RE word. (The AI drafts and a setter's own edit keep adsMention's narrower net, which still refuses the name
    // Get Booked: worker/email/salesDraft.ts.)
    const ad = cannabis ? adsMention(said, { campaigns: true }) ?? said.match(ADS_RE)?.[0] ?? null : null;
    if (ad) p.push(where(`${part}: ads to a cannabis business ("${ad}"; no ads talk at all, not even that they're banned: "Our work is Google and your own website.")`));
  };
  const checkOpenerNever = (part: string, text: string) => {
    const w = openerNeverWord(text);
    if (w) p.push(where(`${part}: scam-call word in the first seconds ("${w}"); never Google, listing, profile, verify, update, claim or suspended there`));
  };
  const spoken = (part: string, text: string, allowed: readonly string[]) => {
    if (!text.trim()) p.push(where(`${part} is empty`));
    checkPlaceholders(part, text, allowed);
    checkNeverSay(part, text);
  };

  // openers
  if (s.openers.length < 2) p.push(where('needs at least 2 openers'));
  if (!s.openers.some((o) => !placeholdersIn(o.text).includes('observation')))
    p.push(where('needs an opener that works without an observation'));
  s.openers.forEach((o, i) => {
    spoken(`opener ${i + 1}`, o.text, ALLOWED.openers);
    checkOpenerNever(`opener ${i + 1}`, o.text);
    const first = o.text.split(/\s+/).slice(0, 14).join(' ');
    if (!first.includes('{setter}') || !first.includes('Valistix'))
      p.push(where(`opener ${i + 1} must say the setter's name and "Valistix" in the first sentence`));
  });
  // US opener rules (name and Valistix, the Default on every lead, a short first sentence); a MIXED campaign can reach
  // US leads too, so it gets the same rules.
  if (t.country === 'US' || t.country === 'MIXED') usOpenerProblems(s.openers).forEach((x) => p.push(where(x)));

  // the first minute
  spoken('reason', s.reason, ALLOWED.spoken);
  if (spokenWords(s.reason) > 45) p.push(where(`reason is ${spokenWords(s.reason)} words; keep it under about 15 seconds (45 words)`));
  // One-screen scripts (REVIEW-2026-10-01): one question, two gatekeeper lines and 5 objection cards are enough.
  if (s.questions.length < 1) p.push(where('needs at least 1 problem question'));
  s.questions.forEach((q, i) => spoken(`question ${i + 1}`, q.text, ALLOWED.spoken));
  spoken('pitch', s.pitch, ALLOWED.spoken);
  spoken('ask', s.ask, ALLOWED.spoken);
  if ((s.ask.match(/\[time\]/g) ?? []).length < 2 || !/tomorrow/i.test(s.ask))
    p.push(where('ask must offer two specific times, tomorrow first'));
  if (s.gatekeeper.length < 2) p.push(where('needs at least 2 gatekeeper lines'));
  s.gatekeeper.forEach((g, i) => {
    spoken(`gatekeeper ${i + 1}`, g.text, ALLOWED.spoken);
    checkOpenerNever(`gatekeeper ${i + 1}`, g.text);
  });

  if (t.offer === 'front_desk') {
    const firstMinute = [...s.openers.map((o) => o.text), s.reason, ...s.gatekeeper.map((g) => g.text), ...s.questions.map((q) => q.text)];
    if (firstMinute.some((x) => FIRST_MINUTE_AI.test(x)))
      p.push(where('says "AI" in an opener, the reason, a question or a gatekeeper line; not in the first minute'));
  }

  // objections
  if (s.objections.length < 3 || s.objections.length > 12) p.push(where(`has ${s.objections.length} objection cards; needs 3-12`));
  const keys = new Set<string>();
  s.objections.forEach((o) => {
    if (keys.has(o.key)) p.push(where(`duplicate objection key ${o.key}`));
    keys.add(o.key);
    if (o.label.length > 24) p.push(where(`objection label "${o.label}" is over 24 characters`));
    if (!(OBJECTIONS as readonly string[]).includes(o.category)) p.push(where(`objection ${o.key} has unknown category ${o.category}`));
    spoken(`objection ${o.key}`, o.response, ALLOWED.objections);
    if (spokenWords(o.response) > 50) p.push(where(`objection ${o.key} response is over 50 words`));
    if (o.followUpQuestion) spoken(`objection ${o.key} follow-up`, o.followUpQuestion, ALLOWED.objections);
  });

  // voicemails read live
  for (const need of ['first', 'second']) {
    if (!s.voicemailsLive.some((v) => v.situation === need)) p.push(where(`needs a live voicemail for situation "${need}"`));
  }
  s.voicemailsLive.forEach((v) => {
    const part = `live voicemail "${v.situation}"`;
    spoken(part, v.text, ALLOWED.voicemailsLive);
    const opening = v.text.split(/\s+/).slice(0, 6).join(' ');
    if (!opening.includes('{setter}') || !opening.includes('Valistix')) p.push(where(`${part} must open with the setter's name and "Valistix"`));
    if (!/\{callback\}/.test(v.text)) p.push(where(`${part} must give {callback}`));
    const words = spokenWords(v.text);
    const limit = v.situation === 'first' ? 35 : 58; // VM1 about 15 s, everything else 25 s max
    if (words > limit) p.push(where(`${part} is ${words} words; limit ${limit}`));
  });

  // recorded drops
  if (t.country === 'CA' && s.voicemailsDrop.length > 0) p.push(where('Canadian campaigns must have no recorded drops (CRTC ADAD rules)'));
  if (t.country === 'US' && s.voicemailsDrop.length === 0) p.push(where('US campaigns need drop scripts for setters to record'));
  s.voicemailsDrop.forEach((v) => {
    const part = `drop "${v.situation}"`;
    spoken(part, v.text, ALLOWED.voicemailsDrop);
    if (!v.text.startsWith('Hi, this is {setter} with Valistix Inc.')) p.push(where(`${part} must open "Hi, this is {setter} with Valistix Inc."`));
    if (!v.text.includes('{callback}')) p.push(where(`${part} must state {callback}`));
    const secs = spokenSeconds(v.text);
    if (secs > 25) p.push(where(`${part} runs about ${secs} s; keep it at 25 s or less`));
  });

  // emails
  for (const need of ['after_vm', 'after_call']) {
    if (!s.emails.some((e) => e.situation === need)) p.push(where(`needs an email for situation "${need}"`));
  }
  s.emails.forEach((e) => {
    const part = `email "${e.situation}"`;
    spoken(`${part} subject`, e.subject, ALLOWED.emails);
    spoken(`${part} body`, e.body, ALLOWED.emails);
    if (e.subject.length > 60) p.push(where(`${part} subject is over 60 characters`));
    if (!e.body.includes('{mailing_address}')) p.push(where(`${part} needs {mailing_address} (CASL, CAN-SPAM)`));
    if (!/reply "stop"/i.test(e.body)) p.push(where(`${part} needs the reply-"stop" opt-out line`));
    if (!e.body.includes('Valistix Inc.')) p.push(where(`${part} must name Valistix Inc.`));
  });

  if (s.disqualifiers.length < 3) p.push(where('needs at least 3 disqualifiers'));
  if (s.complianceNotes.length < 5) p.push(where('needs at least 5 compliance notes'));

  // preferred windows: inside the default calling window (09:00-20:00), never Sunday
  if (t.windows.length === 0) p.push(where('needs preferred calling windows'));
  t.windows.forEach((w, i) => {
    if (!w.days.length || w.days.some((d) => !Number.isInteger(d) || d < 1 || d > 6)) p.push(where(`window ${i + 1} days must be 1-6 (Mon-Sat)`));
    if (!HHMM.test(w.start) || !HHMM.test(w.end) || w.start >= w.end) p.push(where(`window ${i + 1} needs HH:MM start before end`));
    if (w.start < '09:00' || w.end > '20:00') p.push(where(`window ${i + 1} falls outside 09:00-20:00`));
    if (!w.label.trim()) p.push(where(`window ${i + 1} needs a label`));
  });

  // practice personas
  const kinds = t.personas.map((x) => x.kind);
  if (t.personas.length !== 3 || PERSONA_KINDS.some((k) => !kinds.includes(k)))
    p.push(where('needs exactly three personas: gatekeeper, lukewarm, hostile'));
  t.personas.forEach((x) => {
    if (!x.name.trim()) p.push(where(`${x.kind} persona needs a name`));
    if (x.brief.length < 800) p.push(where(`${x.kind} persona brief is thin (${x.brief.length} chars; want 800+)`));
    if (!/fictional/i.test(x.brief)) p.push(where(`${x.kind} persona brief must say the business is fictional`));
  });

  return p;
}

const TRAINING_KINDS: readonly TrainingItem['kind'][] = ['watch', 'read', 'listen', 'drill', 'roleplay', 'quiz', 'shadow'];

export function validateCurriculum(items: ReadonlyArray<Omit<TrainingItem, 'doneAt'>>): string[] {
  const p: string[] = [];
  const keys = new Set<string>();
  let lastDay = 0;
  for (const it of items) {
    const m = /^d(\d+)-[a-z0-9]+(-[a-z0-9]+)*$/.exec(it.key);
    if (!m || Number(m[1]) !== it.day) p.push(`${it.key}: key must be 'd<day>-words' and match day ${it.day}`);
    if (keys.has(it.key)) p.push(`${it.key}: duplicate key`);
    keys.add(it.key);
    if (!Number.isInteger(it.day) || it.day < 1 || it.day > 10) p.push(`${it.key}: day must be 1-10`);
    if (it.day < lastDay) p.push(`${it.key}: items must be in day order`);
    lastDay = it.day;
    if (!TRAINING_KINDS.includes(it.kind)) p.push(`${it.key}: unknown kind ${it.kind}`);
    if (!it.title.trim()) p.push(`${it.key}: title is empty`);
    if (it.url !== null && !/^https:\/\/[^\s]+$/.test(it.url)) p.push(`${it.key}: url must be https or null`);
    if (!Number.isInteger(it.minutes) || it.minutes < 1 || it.minutes > 240) p.push(`${it.key}: minutes must be 1-240`);
    if (it.why.trim().length < 20) p.push(`${it.key}: say why in a sentence`);
  }
  for (let d = 1; d <= 10; d++) if (!items.some((it) => it.day === d)) p.push(`day ${d} has no items`);
  return p;
}
